import { useEffect, useState } from "react";
import { Input, Modal, Skeleton, message } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import LocationsApi from "../../api/LocationsApi";
import { DesignDto } from "../../generated/dashboard-client";
import RendererThumb from "../common/RendererThumb";
import { env } from "../../env/env";
import "./AssignSourceModal.scss";

export interface DesignOption {
  id: string;
  name: string;
  thumbUrl?: string;
}

type SourceTab = "savedDesigns" | "itemLibrary";

interface Props {
  open: boolean;
  locationId?: number;
  onClose: () => void;
  initialDesignId?: string;
  initialAlias?: string;
  onSetSource: (design: DesignOption, alias: string) => void;
}

// Mirrors getDesignThumb in Designs.tsx — uploaded PDFs vs renderer-served designs.
function designThumbUrl(
  design: DesignDto,
  height: number = env.rendererImageHeight
): string {
  if (design.upload) {
    return `/imageservice/images/13/member/uploads/user_${design.id}/thumb_${design.id}1.jpg`;
  }
  return `${env.rendererPath}/design/thumbnail/${design.id}?height=${height}`;
}

export function AssignSourceModal({
  open,
  locationId,
  initialDesignId,
  initialAlias,
  onClose,
  onSetSource,
}: Props) {
  const [tab, setTab] = useState<SourceTab>("savedDesigns");
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [alias, setAlias] = useState("");
  const [designs, setDesigns] = useState<DesignOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelectedId(initialDesignId ?? null);
    setAlias(initialAlias ?? "");
  }, [open, initialDesignId, initialAlias]);

  useEffect(() => {
    if (!open || !locationId) return;
    setLoading(true);
    LocationsApi.getLocationDesigns(locationId)
      .then(({ data }) => {
        setDesigns(
          (data || [])
            .filter((d) => d.visible !== false && d.id)
            .map((d) => ({
              id: d.id!,
              name: d.name || "Untitled",
              thumbUrl: designThumbUrl(d),
            }))
        );
      })
      .catch(() => message.error("Failed to load designs"))
      .finally(() => setLoading(false));
  }, [open, locationId]);

  const filtered = designs.filter((d) =>
    d.name.toLowerCase().includes(query.trim().toLowerCase())
  );
  const selected = designs.find((d) => d.id === selectedId) || null;

  const close = () => {
    setSelectedId(null);
    setAlias("");
    setQuery("");
    setTab("savedDesigns");
    onClose();
  };

  const setAsSource = () => {
    if (!selected) return;
    onSetSource(selected, alias.trim());
    close();
  };

  return (
    <Modal
      open={open}
      onCancel={close}
      footer={null}
      width={760}
      centered
      title="Select Source"
      className="assign-source-modal"
    >
      <div className="asm-tabs">
        <button
          type="button"
          className={`asm-tab${tab === "savedDesigns" ? " active" : ""}`}
          onClick={() => setTab("savedDesigns")}
        >
          Saved Designs
        </button>
        <button
          type="button"
          className={`asm-tab${tab === "itemLibrary" ? " active" : ""}`}
          onClick={() => setTab("itemLibrary")}
        >
          Item Library
        </button>
      </div>

      {tab === "savedDesigns" ? (
        <>
          <Input
            className="asm-search"
            prefix={<SearchOutlined />}
            placeholder="Search by design name"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            allowClear
          />

          <div className="asm-grid">
            {loading ? (
              <div className="asm-loading">
                <Skeleton active />
              </div>
            ) : (
              <>
                {filtered.map((d) => (
                  <button
                    key={d.id}
                    type="button"
                    className={`asm-design${
                      selectedId === d.id ? " selected" : ""
                    }`}
                    onClick={() => setSelectedId(d.id)}
                  >
                    <RendererThumb
                      src={d.thumbUrl}
                      alt={d.name}
                      loading="lazy"
                      className="asm-design-thumb"
                      fallbackSrc={`${env.dashboardPath}/thumb-placeholder.png`}
                    />
                    <div className="asm-design-name">{d.name}</div>
                  </button>
                ))}
                {filtered.length === 0 && (
                  <div className="asm-empty">No designs found.</div>
                )}
              </>
            )}
          </div>

          <div className="asm-alias">
            <label htmlFor="asm-alias-input">Alias (optional)</label>
            <Input
              id="asm-alias-input"
              placeholder={selected?.name || "e.g. Dinner Menu"}
              value={alias}
              maxLength={100}
              onChange={(e) => setAlias(e.target.value)}
            />
            <div className="asm-alias-help">
              The menu name shown on Google, Yelp and other publishers. Defaults
              to the design name.
            </div>
          </div>

          <div className="asm-footer">
            <button
              type="button"
              className="asm-set-btn"
              disabled={!selected}
              onClick={setAsSource}
            >
              Set as source &amp; publish
            </button>
          </div>
        </>
      ) : (
        <div className="asm-phase2">Item Library — coming soon (Phase II).</div>
      )}
    </Modal>
  );
}
